import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useShareAction } from "../contexts/ShareActionContext";
import { DebtsList, DebtForm } from "../components/debts";
import { Debt, Expense } from "../lib/types";
import debtDB from "../services/database/debtDB";
import { expenseAPI } from "../services/api/api";
import { demoService } from "../services/demo/demoService";
import { plaidCreditCardService } from "../services/plaid/plaidCreditCardService";
import { plaidService, PlaidAccount } from "../services/external/plaidService";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import {
  useTheme,
  Box,
  Fab,
  Tooltip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Skeleton,
  Typography,
  Card,
  CardContent,
  TextField,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import HistoryIcon from "@mui/icons-material/History";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import LinkAccountDialog from "../components/debts/LinkAccountDialog";
import ShareDebtModal from "../components/debts/ShareDebtModal";
import PageShareModal from "../components/common/PageShareModal";
import { EmptyState } from "../components/common";
import LoadingTimerDebug from "../components/common/LoadingTimerDebug";

interface DebtsPageProps {
  setPageTitle?: (title: string) => void;
}

type DebtOverrideField = keyof Pick<
  Debt,
  | "balance"
  | "creditLimit"
  | "maxMonthlyPayment"
  | "paydownAmount"
  | "dueDate"
  | "status"
  | "payoffStrategy"
  | "creditAffecting"
  | "statementCredits"
  | "type"
>;

type DebtImportHistoryEntry = {
  id: string;
  createdAt: string;
  fileName?: string;
  totalCount: number;
  addedCount: number;
  updatedCount: number;
};

const importHistoryStorageKey = "debtImportHistory";
const plaidOverrideStorageKey = "plaidDebtOverrides";

type PlaidDebtOverride = {
  creditor?: string;
  type?: string;
  creditAffecting?: string;
  status?: string;
  dueDate?: string;
  statementCredits?: number;
  maxMonthlyPayment?: number;
  paydownAmount?: number;
  creditLimit?: number;
  debtQuality?: Debt["debtQuality"];
  canDefault?: boolean;
  payoffStrategy?: string;
};

const debtOverrideFields: { key: DebtOverrideField; label: string }[] = [
  { key: "balance", label: "Balance" },
  { key: "creditLimit", label: "Credit Limit" },
  { key: "maxMonthlyPayment", label: "Max Monthly Payment" },
  { key: "paydownAmount", label: "Paydown Amount" },
  { key: "dueDate", label: "Due Date" },
  { key: "status", label: "Status" },
  { key: "payoffStrategy", label: "Payoff Strategy" },
  { key: "creditAffecting", label: "Credit Affecting" },
  { key: "statementCredits", label: "Statement Credits" },
  { key: "type", label: "Type" },
];

const normalizeText = (value: string) => value.trim().replace(/\s+/g, " ");

const normalizeAccount = (value: string) => normalizeText(value).toLowerCase();

const normalizeDebtKey = (account: string, creditor?: string) => {
  const accountKey = normalizeAccount(account);
  const creditorKey = normalizeAccount(creditor || "");
  return `${creditorKey}::${accountKey}`;
};

const splitCreditorFromAccount = (value: string) => {
  const normalized = normalizeText(value);
  if (!normalized) return null;

  const patterns = [
    /^(.*?)[\s-–—#:]+(?:acct|account|ending|ending in|ending with)?\s*(\d{3,6})$/i,
    /^(.*?)[([]\s*(?:acct|account|ending|ending in|ending with)?\s*(\d{3,6})\s*[)\]]$/i,
    /^(.*?)(?:\s+|.*?)(?:\*{2,}|x{2,}|\u2022{2,})\s*(\d{3,6})$/i,
  ];

  for (const pattern of patterns) {
    const match = normalized.match(pattern);
    if (!match) continue;
    const creditor = match[1]
      .trim()
      .replace(/[\s-–—#:]+$/, "")
      .trim();
    const account = match[2].trim();
    if (!creditor || !/[A-Za-z]/.test(creditor)) continue;
    return { creditor, account };
  }

  return null;
};

const isAccountNumberOrEmail = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  // Check if it's primarily numeric (account number) - allows some dashes/spaces
  const numericPattern = /^[\d\s\-*x]+$/i;
  if (numericPattern.test(trimmed)) return true;
  // Check if it's an email address
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (emailPattern.test(trimmed)) return true;
  // Check if it ends with digits (like "...1234" or "ending 5678")
  const endsWithDigits = /\d{3,}$/;
  if (endsWithDigits.test(trimmed) && trimmed.length <= 10) return true;
  return false;
};

const DebtsPage: React.FC<DebtsPageProps> = ({ setPageTitle }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const importInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();
  const editParamHandled = useRef(false);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaidLoading, setIsPlaidLoading] = useState(false);
  const [isAccountsLoading, setIsAccountsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentDebt, setCurrentDebt] = useState<Debt | undefined>(undefined);
  const [duplicates, setDuplicates] = useState<
    Array<{ manual: Debt; plaid: any }>
  >([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isImportHistoryOpen, setIsImportHistoryOpen] = useState(false);
  const [importHistory, setImportHistory] = useState<DebtImportHistoryEntry[]>(
    []
  );
  const [importFileName, setImportFileName] = useState<string>("");
  const [importNewDebts, setImportNewDebts] = useState<Omit<Debt, "id">[]>([]);
  const [importMatches, setImportMatches] = useState<
    Array<{ existing: Debt; incoming: Omit<Debt, "id"> }>
  >([]);
  const [overrideSelections, setOverrideSelections] = useState<
    Record<DebtOverrideField, boolean>
  >({
    balance: true,
    creditLimit: true,
    maxMonthlyPayment: true,
    paydownAmount: false,
    dueDate: true,
    status: true,
    payoffStrategy: false,
    creditAffecting: false,
    statementCredits: false,
    type: false,
  });
  const [accounts, setAccounts] = useState<PlaidAccount[]>([]);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [debtToLink, setDebtToLink] = useState<Debt | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareDebt, setShareDebt] = useState<Debt | null>(null);
  const [pageShareOpen, setPageShareOpen] = useState(false);

  const isPlaidDebt = (debt: Debt) =>
    debt.id?.startsWith("plaid-") ||
    ("isPlaidAccount" in debt && (debt as any).isPlaidAccount);

  const readPlaidOverrides = (): Record<string, PlaidDebtOverride> => {
    try {
      const stored = localStorage.getItem(plaidOverrideStorageKey);
      if (!stored) return {};
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      console.warn("Failed to read Plaid debt overrides:", error);
      return {};
    }
  };

  const writePlaidOverrides = (
    overrides: Record<string, PlaidDebtOverride>
  ) => {
    try {
      localStorage.setItem(plaidOverrideStorageKey, JSON.stringify(overrides));
    } catch (error) {
      console.warn("Failed to store Plaid debt overrides:", error);
    }
  };

  const applyPlaidOverrides = useCallback(<T extends Debt>(cards: T[]): T[] => {
    const plaidOverrides = readPlaidOverrides();
    return cards.map((card) => {
      const plaidAccountId =
        "plaidAccountId" in card ? (card as any).plaidAccountId : undefined;
      const override =
        (plaidAccountId && plaidOverrides[plaidAccountId]) ||
        plaidOverrides[card.id] ||
        null;
      if (!override) return card;
      return { ...card, ...override } as T;
    });
  }, []);

  // Auto-categorize debt based on account name
  // const categorizeDebt = (account: string): string => {
  //   const accountLower = account.toLowerCase();
  //
  //   if (accountLower.includes('toyota') || accountLower.includes('rv') || accountLower.includes('car') || accountLower.includes('auto')) {
  //     return 'Auto Loans';
  //   }
  //   if (accountLower.includes('paypal') || accountLower.includes('stripe') || accountLower.includes('business')) {
  //     return 'Business Loans';
  //   }
  //   if (accountLower.includes('tax') || accountLower.includes('irs') || accountLower.includes('state') || accountLower.includes('federal')) {
  //     return 'Taxes';
  //   }
  //   if (accountLower.includes('card') || accountLower.includes('amazon') || accountLower.includes('apple') ||
  //       accountLower.includes('visa') || accountLower.includes('mastercard') || accountLower.includes('american express') ||
  //       accountLower.includes('discover') || accountLower.includes('best buy') || accountLower.includes('synchrony') ||
  //       accountLower.includes('affirm') || accountLower.includes('creditone') || accountLower.includes('hilton') ||
  //       accountLower.includes('kay jewelers')) {
  //     return 'Credit Cards';
  //   }
  //   if (accountLower.includes('medical') || accountLower.includes('physician') || accountLower.includes('emergency') ||
  //       accountLower.includes('hospital') || accountLower.includes('therapy')) {
  //     return 'Medical Loans';
  //   }
  //   return 'Other';
  // };

  // Initial debt data - simplified since data already exists in database
  const getInitialDebtData = (): Omit<Debt, "id">[] => {
    return []; // Data is already in database
  };

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle("Debts");
    }
  }, [setPageTitle]);

  const applyAccountSplit = useCallback(
    <T extends Omit<Debt, "id"> | Debt>(debt: T) => {
      // If creditor already exists, no changes needed
      if (debt.creditor && debt.creditor.trim()) return debt;

      // First try the pattern-based split (e.g., "Chase - 1234")
      const split = splitCreditorFromAccount(debt.account);
      if (split) {
        return { ...debt, creditor: split.creditor, account: split.account };
      }

      // If account is not a number or email, move it to creditor
      if (debt.account && !isAccountNumberOrEmail(debt.account)) {
        return { ...debt, creditor: debt.account, account: "" };
      }

      return debt;
    },
    []
  );

  useEffect(() => {
    const stored = localStorage.getItem(importHistoryStorageKey);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as DebtImportHistoryEntry[];
      if (Array.isArray(parsed)) {
        setImportHistory(parsed);
      }
    } catch (error) {
      console.warn("Failed to parse debt import history:", error);
    }
  }, []);

  // Load or import debts and expenses
  useEffect(() => {
    const loadData = async () => {
      const loadStart = performance.now();
      try {
        setIsLoading(true);

        const [debtsResult, expensesResult] = await Promise.all([
          debtDB.getAllDebts(),
          expenseAPI.getAll(),
        ]);

        let allDebts = debtsResult;

        console.debug("[DebtsPage] Base data loaded", {
          debts: debtsResult.length,
          expenses: expensesResult.length,
          ms: Math.round(performance.now() - loadStart),
        });

        // If no debts exist, import the initial data (only if there's data to import)
        if (allDebts.length === 0) {
          const initialDebts = getInitialDebtData();
          if (initialDebts.length > 0) {
            allDebts = await debtDB.importInitialData(initialDebts);
          }
        }

        const debtUpdates: Debt[] = [];
        const normalizedDebts = allDebts.map((debt) => {
          const next = applyAccountSplit(debt) as Debt;
          if (
            next.account !== debt.account ||
            (next.creditor || "") !== (debt.creditor || "")
          ) {
            debtUpdates.push(next);
          }
          return next;
        });

        allDebts = normalizedDebts;

        setDebts(allDebts);
        setExpenses(expensesResult);
        setDuplicates([]);

        if (debtUpdates.length > 0) {
          void Promise.allSettled(
            debtUpdates.map((debt) => debtDB.updateDebt(debt))
          ).then((results) => {
            const failures = results.filter((r) => r.status === "rejected");
            if (failures.length > 0) {
              console.warn(
                "[DebtsPage] Debt normalization updates failed",
                failures
              );
            }
          });
        }

        setIsPlaidLoading(true);
        const plaidStart = performance.now();
        try {
          const plaidCreditCards =
            await plaidCreditCardService.getCreditCardAccounts();
          const plaidCreditCardsWithOverrides =
            applyPlaidOverrides(plaidCreditCards);

          const allDebtsWithPlaid = [
            ...allDebts,
            ...plaidCreditCardsWithOverrides,
          ];

          const potentialDuplicates =
            plaidCreditCardService.findPotentialDuplicates(
              allDebts,
              plaidCreditCardsWithOverrides
            );

          setDebts(allDebtsWithPlaid);
          setDuplicates(potentialDuplicates);
          console.debug("[DebtsPage] Plaid data loaded", {
            plaidDebts: plaidCreditCardsWithOverrides.length,
            totalDebts: allDebtsWithPlaid.length,
            ms: Math.round(performance.now() - plaidStart),
          });
        } catch (error) {
          console.error("Failed to load Plaid data:", error);
        } finally {
          setIsPlaidLoading(false);
        }
      } catch (error) {
        console.error("Failed to load data:", error);
      } finally {
        setIsLoading(false);
        console.debug("[DebtsPage] Load complete", {
          ms: Math.round(performance.now() - loadStart),
        });
      }
    };

    loadData();

    // Subscribe to demo mode changes
    const unsubscribe = demoService.subscribe(() => {
      loadData(); // Reload data when demo mode changes
    });

    return unsubscribe;
  }, [applyAccountSplit, applyPlaidOverrides]);

  const parseCsv = (text: string): string[][] => {
    const rows: string[][] = [];
    let row: string[] = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i += 1) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          field += '"';
          i += 1;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        row.push(field);
        field = "";
      } else if (char === "\n") {
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else if (char !== "\r") {
        field += char;
      }
    }

    if (field.length || row.length) {
      row.push(field);
      rows.push(row);
    }

    return rows.filter((r) => r.some((value) => value.trim() !== ""));
  };

  const parseAmount = (value: string): number => {
    if (!value) return 0;
    const cleaned = value.replace(/[$,]/g, "").replace(/[()]/g, "").trim();
    const parsed = Number.parseFloat(cleaned);
    if (Number.isNaN(parsed)) return 0;
    return Math.abs(parsed);
  };

  const handleImportClick = () => {
    importInputRef.current?.click();
  };

  const handleImportFile = async (file: File) => {
    try {
      setIsImporting(true);
      setImportFileName(file.name);

      const text = await file.text();
      const rows = parseCsv(text);
      const header = rows[0]?.map((value) => value.trim()) || [];

      const col = (name: string) => header.indexOf(name);
      const accountIdx = col("Account");
      const balanceIdx = col("Balance");
      const creditorIdx = col("Creditor");
      const currentMinIdx = col("Current Min. Due");
      const payoffStrategyIdx = col("Payoff Strategy");
      const creditAffectingIdx = col("Credit Affecting");
      const creditLimitIdx = col("Credit Limit");
      const paydownAmountIdx = col("Paydown Amount");
      const maxPaymentIdx = col("Max Monthly Payment");
      const dueDateIdx = col("Due Date");
      const statusIdx = col("Status");
      const standingIdx = col("Standing");
      const statementCreditsIdx = col("Statement Credits");
      const purposeIdx = col("Purpose");

      if (accountIdx === -1 || balanceIdx === -1) {
        throw new Error("Missing required columns in CSV");
      }

      const incomingDebts = rows
        .slice(1)
        .map((row) => {
          const rawAccount = (row[accountIdx] || "").trim();
          if (!rawAccount) return null;
          const creditorFromColumn = (row[creditorIdx] || "").trim();
          const split = splitCreditorFromAccount(rawAccount);
          const account = (split?.account || rawAccount).trim();
          const creditor = creditorFromColumn || split?.creditor || undefined;
          if (!account) return null;

          const balance = parseAmount(row[balanceIdx] || "");
          const creditLimit = parseAmount(row[creditLimitIdx] || "");
          const maxMonthlyPayment = parseAmount(
            row[maxPaymentIdx] || row[currentMinIdx] || ""
          );
          const paydownAmount = parseAmount(row[paydownAmountIdx] || "");
          const statementCredits = parseAmount(row[statementCreditsIdx] || "");
          const dueDate = (row[dueDateIdx] || "").trim();
          const status =
            (row[statusIdx] || "").trim() ||
            (row[standingIdx] || "").trim() ||
            "Active";
          const payoffStrategy = (row[payoffStrategyIdx] || "").trim();
          const creditAffecting = (row[creditAffectingIdx] || "").trim();
          const purpose = (row[purposeIdx] || "").trim();
          const accountLower = account.toLowerCase();
          const inferredCredit =
            creditLimit > 0 ||
            accountLower.includes("card") ||
            accountLower.includes("visa") ||
            accountLower.includes("mastercard") ||
            accountLower.includes("amex") ||
            accountLower.includes("american express");
          const type = purpose || (inferredCredit ? "Credit Card" : "Other");

          return {
            account,
            creditor,
            type,
            balance,
            payoffStrategy,
            creditAffecting,
            creditLimit,
            paydownAmount,
            maxMonthlyPayment,
            dueDate,
            status,
            statementCredits,
          };
        })
        .filter((debt) => debt !== null) as Omit<Debt, "id">[];

      const manualDebts = debts.filter(
        (debt) => !("isPlaidAccount" in debt && debt.isPlaidAccount)
      );
      const manualMap = new Map(
        manualDebts.map((debt) => [
          normalizeDebtKey(debt.account, debt.creditor),
          debt,
        ])
      );

      const matches: Array<{ existing: Debt; incoming: Omit<Debt, "id"> }> = [];
      const additions: Omit<Debt, "id">[] = [];

      incomingDebts.forEach((incoming) => {
        const match = manualMap.get(
          normalizeDebtKey(incoming.account, incoming.creditor)
        );
        if (match) {
          matches.push({ existing: match, incoming });
        } else {
          additions.push(incoming);
        }
      });

      setImportMatches(matches);
      setImportNewDebts(additions);
      setIsImportDialogOpen(true);
    } catch (error) {
      console.error("Failed to import debts CSV:", error);
      alert("Debt import failed. Please check the CSV format.");
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = "";
    await handleImportFile(file);
  };

  const applyOverrides = (existing: Debt, incoming: Omit<Debt, "id">): Debt => {
    const updated: Debt = { ...existing };
    debtOverrideFields.forEach((field) => {
      if (overrideSelections[field.key]) {
        (updated as unknown as Record<string, unknown>)[field.key] =
          incoming[field.key];
      }
    });
    return updated;
  };

  const handleConfirmImport = async () => {
    try {
      setIsImporting(true);
      const updatedDebts: Debt[] = [];
      const newDebts: Debt[] = [];

      for (const match of importMatches) {
        const updated = applyOverrides(match.existing, match.incoming);
        const saved = await debtDB.updateDebt(updated);
        updatedDebts.push(saved);
      }

      for (const incoming of importNewDebts) {
        const saved = await debtDB.addDebt(incoming);
        newDebts.push(saved);
      }

      if (updatedDebts.length > 0 || newDebts.length > 0) {
        const updatedMap = new Map(updatedDebts.map((debt) => [debt.id, debt]));
        const merged = debts.map((debt) => updatedMap.get(debt.id) || debt);
        const nextDebts = [...merged, ...newDebts];
        setDebts(nextDebts);

        const manualDebts = nextDebts.filter(
          (debt) => !("isPlaidAccount" in debt && debt.isPlaidAccount)
        );
        const plaidDebts = nextDebts.filter(
          (debt) => "isPlaidAccount" in debt && debt.isPlaidAccount
        ) as any;
        const potentialDuplicates =
          plaidCreditCardService.findPotentialDuplicates(
            manualDebts,
            plaidDebts
          );
        setDuplicates(potentialDuplicates);
      }

      const totalCount = importMatches.length + importNewDebts.length;
      if (totalCount > 0) {
        const entryId =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        const historyEntry: DebtImportHistoryEntry = {
          id: entryId,
          createdAt: new Date().toISOString(),
          fileName: importFileName || undefined,
          totalCount,
          addedCount: importNewDebts.length,
          updatedCount: importMatches.length,
        };
        const nextHistory = [historyEntry, ...importHistory];
        setImportHistory(nextHistory);
        localStorage.setItem(
          importHistoryStorageKey,
          JSON.stringify(nextHistory)
        );
      }
    } catch (error) {
      console.error("Failed to apply debt import:", error);
      alert("Debt import failed while saving. Please try again.");
    } finally {
      setIsImporting(false);
      setIsImportDialogOpen(false);
      setImportMatches([]);
      setImportNewDebts([]);
      setImportFileName("");
    }
  };

  const handleToggleOverride = (field: DebtOverrideField) => {
    setOverrideSelections((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const onOpen = () => {
    setIsEditing(false);
    setCurrentDebt(undefined);
    setIsOpen(true);
  };

  const onClose = () => setIsOpen(false);

  const handleAddDebt = async (debtData: Omit<Debt, "id">) => {
    try {
      const preparedDebt = applyAccountSplit(debtData) as Omit<Debt, "id">;
      console.log("[DebtsPage] Adding debt:", preparedDebt);
      // Add to database
      const newDebt = await debtDB.addDebt(preparedDebt);

      // Update local state
      setDebts((prevDebts) => [...prevDebts, newDebt]);
      onClose();
    } catch (error) {
      console.error("Failed to add debt:", error);
      alert("Failed to save debt. Please try again.");
    }
  };

  const handleEditDebt = useCallback((debt: Debt) => {
    setCurrentDebt(debt);
    setIsEditing(true);
    setIsOpen(true);
  }, []);

  useEffect(() => {
    if (editParamHandled.current || isLoading) return;
    const editDebtId = searchParams.get("editDebtId");
    if (!editDebtId) return;

    const matchingDebt = debts.find((debt) => debt.id === editDebtId);
    console.debug("[DebtsPage] Handle editDebtId query param", {
      editDebtId,
      found: Boolean(matchingDebt),
    });
    if (matchingDebt) {
      handleEditDebt(matchingDebt);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("editDebtId");
    setSearchParams(nextParams, { replace: true });
    editParamHandled.current = true;
  }, [debts, handleEditDebt, isLoading, searchParams, setSearchParams]);

  useEffect(() => {
    const shareParam = searchParams.get("share");
    if (shareParam === "1" && !pageShareOpen) {
      setPageShareOpen(true);
    }
  }, [pageShareOpen, searchParams]);

  useEffect(() => {
    setShareHandler(() => {
      const next = new URLSearchParams(searchParams);
      next.set("share", "1");
      setSearchParams(next, { replace: true });
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [searchParams, setSearchParams, setShareHandler]);

  const handleUpdateDebt = async (updatedDebt: Debt) => {
    try {
      console.log("[DebtsPage] Updating debt:", updatedDebt);
      // Update in database
      await debtDB.updateDebt(updatedDebt);

      // Update local state
      setDebts((prevDebts) =>
        prevDebts.map((debt) =>
          debt.id === updatedDebt.id ? updatedDebt : debt
        )
      );

      onClose();
    } catch (error) {
      console.error("Failed to update debt:", error);
      alert("Failed to update debt. Please try again.");
    }
  };

  // Handler for inline editing (doesn't close form panel)
  const handleInlineUpdateDebt = async (updatedDebt: Debt) => {
    try {
      if (isPlaidDebt(updatedDebt)) {
        const plaidAccountId =
          "plaidAccountId" in updatedDebt
            ? (updatedDebt as any).plaidAccountId
            : undefined;
        const overrideKey = plaidAccountId || updatedDebt.id;
        const existingDebt = debts.find((debt) => debt.id === updatedDebt.id);
        const overrides = readPlaidOverrides();
        const prevOverride = overrides[overrideKey] || {};
        overrides[overrideKey] = {
          ...prevOverride,
          creditor:
            updatedDebt.creditor !== existingDebt?.creditor
              ? updatedDebt.creditor
              : prevOverride.creditor,
          type:
            updatedDebt.type !== existingDebt?.type
              ? updatedDebt.type
              : prevOverride.type,
          creditAffecting:
            updatedDebt.creditAffecting !== existingDebt?.creditAffecting
              ? updatedDebt.creditAffecting
              : prevOverride.creditAffecting,
          status:
            updatedDebt.status !== existingDebt?.status
              ? updatedDebt.status
              : prevOverride.status,
          dueDate:
            updatedDebt.dueDate !== existingDebt?.dueDate
              ? updatedDebt.dueDate
              : prevOverride.dueDate,
          statementCredits:
            updatedDebt.statementCredits !== existingDebt?.statementCredits
              ? updatedDebt.statementCredits
              : prevOverride.statementCredits,
          maxMonthlyPayment:
            updatedDebt.maxMonthlyPayment !== existingDebt?.maxMonthlyPayment
              ? updatedDebt.maxMonthlyPayment
              : prevOverride.maxMonthlyPayment,
          paydownAmount:
            updatedDebt.paydownAmount !== existingDebt?.paydownAmount
              ? updatedDebt.paydownAmount
              : prevOverride.paydownAmount,
          creditLimit:
            updatedDebt.creditLimit !== existingDebt?.creditLimit
              ? updatedDebt.creditLimit
              : prevOverride.creditLimit,
          debtQuality:
            updatedDebt.debtQuality !== existingDebt?.debtQuality
              ? updatedDebt.debtQuality
              : prevOverride.debtQuality,
          canDefault:
            updatedDebt.canDefault !== existingDebt?.canDefault
              ? updatedDebt.canDefault
              : prevOverride.canDefault,
          payoffStrategy: updatedDebt.payoffStrategy,
        };
        writePlaidOverrides(overrides);

        setDebts((prevDebts) =>
          prevDebts.map((debt) =>
            debt.id === updatedDebt.id ? { ...debt, ...updatedDebt } : debt
          )
        );
        return;
      }

      // Update in database
      await debtDB.updateDebt(updatedDebt);

      // Update local state
      setDebts((prevDebts) =>
        prevDebts.map((debt) =>
          debt.id === updatedDebt.id ? updatedDebt : debt
        )
      );
    } catch (error) {
      console.error("Failed to update debt:", error);
      alert("Failed to update debt. Please try again.");
    }
  };

  const handleDeleteDebt = async (id: string) => {
    try {
      // Delete from database
      await debtDB.deleteDebt(id);

      // Update local state
      setDebts((prevDebts) => prevDebts.filter((debt) => debt.id !== id));
    } catch (error) {
      console.error("Failed to delete debt:", error);
      alert("Failed to delete debt. Please try again.");
    }
  };

  const handleLinkAccount = async (debt: Debt) => {
    setDebtToLink(debt);
    setIsLinkDialogOpen(true);
    if (accounts.length === 0 && !isAccountsLoading) {
      setIsAccountsLoading(true);
      try {
        const allAccounts = await plaidService.getAccounts();
        setAccounts(allAccounts);
        console.debug("[DebtsPage] Loaded accounts for linking", {
          count: allAccounts.length,
        });
      } catch (error) {
        console.error("Failed to load accounts for linking:", error);
      } finally {
        setIsAccountsLoading(false);
      }
    }
  };

  const handleShareDebt = (debt: Debt) => {
    setShareDebt(debt);
    setShareOpen(true);
  };

  const handleSelectAccountToLink = async (accountId: string | null) => {
    if (!debtToLink) return;

    try {
      const updatedDebt: Debt = {
        ...debtToLink,
        linkedAccountId: accountId || undefined,
      };

      // Update in database
      await debtDB.updateDebt(updatedDebt);

      // Update local state
      setDebts((prevDebts) =>
        prevDebts.map((debt) =>
          debt.id === updatedDebt.id ? updatedDebt : debt
        )
      );

      setIsLinkDialogOpen(false);
      setDebtToLink(null);
    } catch (error) {
      console.error("Failed to link account:", error);
      alert("Failed to link account. Please try again.");
    }
  };

  const handleRefreshPlaidBalances = async () => {
    try {
      setIsRefreshing(true);
      setIsPlaidLoading(true);

      // Fetch fresh Plaid credit card data (force refresh to bypass cache)
      const plaidCreditCards =
        await plaidCreditCardService.getCreditCardAccounts(true);
      const plaidCreditCardsWithOverrides =
        applyPlaidOverrides(plaidCreditCards);

      // Get current manual debts (filter out old Plaid accounts)
      const manualDebts = debts.filter(
        (debt) => !("isPlaidAccount" in debt && debt.isPlaidAccount)
      );

      // Merge with fresh Plaid data
      const updatedDebts = [...manualDebts, ...plaidCreditCardsWithOverrides];

      // Detect duplicates with fresh data
      const potentialDuplicates =
        plaidCreditCardService.findPotentialDuplicates(
          manualDebts,
          plaidCreditCardsWithOverrides
        );

      setDebts(updatedDebts);
      setDuplicates(potentialDuplicates);
    } catch (error) {
      console.error("Failed to refresh Plaid balances:", error);
      alert("Failed to refresh balances. Please try again.");
    } finally {
      setIsRefreshing(false);
      setIsPlaidLoading(false);
    }
  };

  // Dark mode aware styles
  const pageContainerStyle = {
    width: "100%",
    padding: "24px 16px",
    boxSizing: "border-box" as "border-box",
    backgroundColor: isDarkMode ? "#0f0f0f" : "#f9fafb",
    minHeight: "100vh",
  };

  return (
    <div style={pageContainerStyle}>
      {isLoading ? (
        <Box sx={{ width: "100%" }}>
          {/* Summary Cards with Skeleton Values */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(4, 1fr)",
                lg: "repeat(7, 1fr)",
              },
              gap: 2,
              mb: 4,
            }}
          >
            {/* Balance */}
            <Card
              sx={{
                borderTop: "4px solid #e53e3e",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Balance
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
            {/* Credit Limit */}
            <Card
              sx={{
                borderTop: "4px solid #38a169",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Credit Limit
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
            {/* Available Credit */}
            <Card
              sx={{
                borderTop: "4px solid #38a169",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Available Credit
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
            {/* Credit Utilization */}
            <Card
              sx={{
                borderTop: `4px solid ${isDarkMode ? "#555" : "#ccc"}`,
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Credit Utilization
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Skeleton width={30} height={24} />
                  <Skeleton width={30} height={24} />
                  <Skeleton width={30} height={24} />
                </Box>
              </CardContent>
            </Card>
            {/* Paydown */}
            <Card
              sx={{
                borderTop: "4px solid #dd6b20",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Paydown
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
            {/* Monthly Payments */}
            <Card
              sx={{
                borderTop: "4px solid #e53e3e",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Monthly Payments
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
            {/* Credits */}
            <Card
              sx={{
                borderTop: "4px solid #319795",
                height: "100%",
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                boxShadow: isDarkMode
                  ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                  : "0 1px 2px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Credits
                </Typography>
                <Skeleton width={80} height={24} />
              </CardContent>
            </Card>
          </Box>

          {/* Search and Filter Bar */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 3,
              p: 2,
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              borderRadius: "8px",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 3px rgba(0,0,0,0.1)",
              alignItems: "center",
            }}
          >
            <FilterListIcon
              sx={{ color: isDarkMode ? "rgba(255,255,255,0.7)" : "#4a5568" }}
            />
            <TextField
              size="small"
              placeholder="Search accounts..."
              disabled
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }} disabled>
              <InputLabel>Status</InputLabel>
              <Select value="all" label="Status">
                <MenuItem value="all">All Statuses</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled>
              <InputLabel>Type</InputLabel>
              <Select value="all" label="Type">
                <MenuItem value="all">All Types</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Table with Header and Skeleton Rows */}
          <Box
            sx={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              borderRadius: "8px",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 3px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead>
                <tr
                  style={{
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                    textAlign: "left",
                  }}
                >
                  {[
                    "Status",
                    "Type",
                    "Creditor",
                    "Account",
                    "Balance",
                    "Limit",
                    "Available",
                    "Utilization",
                    "Paydown",
                    "Monthly",
                    "Due",
                    "Credits",
                    "Payoff Strategy",
                    "Credit Affecting",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      style={{
                        padding: "6px 4px",
                        borderBottom: isDarkMode
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid #e2e8f0",
                        color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 8 }).map((_, index) => (
                  <tr
                    key={index}
                    style={{
                      borderBottom: isDarkMode
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "1px solid #e2e8f0",
                    }}
                  >
                    {Array.from({ length: 15 }).map((_, colIndex) => (
                      <td key={colIndex} style={{ padding: "6px 4px" }}>
                        <Skeleton width={colIndex < 4 ? 60 : 50} height={20} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        </Box>
      ) : (
        <>
          {debts.length === 0 ? (
            <EmptyState
              icon={<CreditCardIcon />}
              title="No Debts Yet"
              description="Track your debts, credit cards, and loans to stay on top of your financial obligations and plan your payoff strategy."
              buttonText="Add Your First Debt"
              onAction={onOpen}
            />
          ) : (
            <>
              {/* Duplicate Detection Warning */}
              {duplicates.length > 0 && (
                <div
                  style={{
                    backgroundColor: isDarkMode ? "#2d2013" : "#fef3c7",
                    border: `2px solid ${isDarkMode ? "#f59e0b" : "#fbbf24"}`,
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      color: isDarkMode ? "#fbbf24" : "#92400e",
                      marginBottom: "12px",
                    }}
                  >
                    ⚠️ Potential Duplicate Accounts Detected
                  </h3>
                  <p
                    style={{
                      fontSize: "14px",
                      color: isDarkMode ? "#fde68a" : "#78350f",
                      marginBottom: "12px",
                    }}
                  >
                    We found {duplicates.length} manual{" "}
                    {duplicates.length === 1 ? "account" : "accounts"} that may
                    be duplicates of your Plaid credit cards. Consider deleting
                    the manual entries to avoid double-counting.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {duplicates.map((dup, index) => (
                      <div
                        key={index}
                        style={{
                          backgroundColor: isDarkMode ? "#1f1f1f" : "white",
                          padding: "12px",
                          borderRadius: "6px",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div>
                          <p
                            style={{
                              fontSize: "14px",
                              fontWeight: "bold",
                              color: isDarkMode ? "#ffffff" : "#111827",
                              marginBottom: "4px",
                            }}
                          >
                            Manual: {dup.manual.account} ($
                            {dup.manual.balance.toFixed(2)})
                          </p>
                          <p
                            style={{
                              fontSize: "13px",
                              color: isDarkMode ? "#9ca3af" : "#6b7280",
                            }}
                          >
                            Plaid: {dup.plaid.account} ($
                            {dup.plaid.balance.toFixed(2)})
                          </p>
                        </div>
                        <button
                          onClick={async () => {
                            if (
                              window.confirm(
                                `Delete manual account "${dup.manual.account}"?`
                              )
                            ) {
                              await handleDeleteDebt(dup.manual.id);
                              // Remove from duplicates list
                              setDuplicates((prev) =>
                                prev.filter(
                                  (d) => d.manual.id !== dup.manual.id
                                )
                              );
                            }
                          }}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "bold",
                          }}
                        >
                          Delete Manual Entry
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <DebtsList
                debts={debts}
                expenses={expenses}
                plaidLoading={isPlaidLoading}
                onEditDebt={handleEditDebt}
                onDeleteDebt={handleDeleteDebt}
                onLinkAccount={handleLinkAccount}
                onUpdateDebt={handleInlineUpdateDebt}
                onShareDebt={handleShareDebt}
              />
            </>
          )}
        </>
      )}

      {/* Add/Edit Debt Form */}
      {isOpen && (
        <>
          {/* Overlay for mobile - clicking outside closes the panel */}
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.5)",
              zIndex: 999,
              display: "block",
            }}
            onClick={onClose}
          />

          {/* Form panel */}
          <div
            style={{
              position: "fixed",
              top: "0",
              right: "0",
              height: "100vh",
              width: "90%", // More responsive width
              maxWidth: "450px", // Maximum width for larger screens
              overflowX: "hidden",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "-2px 0 10px rgba(0,0,0,0.5)"
                : "-2px 0 10px rgba(0,0,0,0.1)",
              zIndex: "1000",
              padding: "24px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
                borderBottom: isDarkMode
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid #e2e8f0",
                paddingBottom: "12px",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: isDarkMode ? "#ffffff" : "#2d3748",
                }}
              >
                {isEditing ? "Edit Debt" : "Add Debt"}
              </h2>
              <button
                onClick={onClose}
                style={{
                  padding: "8px 12px",
                  backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                  border: isDarkMode
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid #e2e8f0",
                  borderRadius: "4px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  color: isDarkMode ? "#e2e8f0" : "#4a5568",
                  fontWeight: "medium",
                }}
              >
                <span>✕</span> Close
              </button>
            </div>

            <div style={{ flex: 1, overflowY: "auto" }}>
              <DebtForm
                onAddDebt={handleAddDebt}
                onUpdateDebt={handleUpdateDebt}
                debtToEdit={currentDebt}
                isEditing={isEditing}
                expenses={expenses}
                isDarkMode={isDarkMode}
                onCancel={onClose}
              />
            </div>
          </div>
        </>
      )}

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Debt actions">
        <input
          ref={importInputRef}
          type="file"
          accept=".csv"
          style={{ display: "none" }}
          onChange={handleImportFileChange}
        />
        <Tooltip title="Import Budget & Forecast CSV" placement="left" arrow>
          <Fab
            color="info"
            size="large"
            onClick={handleImportClick}
            disabled={isImporting}
            sx={{
              opacity: isImporting ? 0.5 : 1,
              ...(theme.palette.mode === "dark" && {
                bgcolor: theme.palette.info.dark,
                "&:hover": {
                  bgcolor: theme.palette.info.main,
                },
              }),
            }}
          >
            {isImporting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <FileUploadIcon />
            )}
          </Fab>
        </Tooltip>
        {/* Refresh Balances FAB */}
        <Tooltip title="Refresh Balances" placement="left" arrow>
          <Fab
            color="secondary"
            size="large"
            onClick={handleRefreshPlaidBalances}
            disabled={isRefreshing}
            sx={{
              opacity: isRefreshing ? 0.5 : 1,
              ...(theme.palette.mode === "dark" && {
                bgcolor: theme.palette.secondary.dark,
                "&:hover": {
                  bgcolor: theme.palette.secondary.main,
                },
              }),
            }}
          >
            {isRefreshing ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <RefreshIcon />
            )}
          </Fab>
        </Tooltip>

        {/* View Import History FAB */}
        <Tooltip title="View Import History" placement="left" arrow>
          <Fab
            color="default"
            size="large"
            onClick={() => setIsImportHistoryOpen(true)}
          >
            <HistoryIcon />
          </Fab>
        </Tooltip>

        {/* Add New Debt FAB */}
        <Tooltip title="Add New Debt" placement="left" arrow>
          <Fab
            color="primary"
            size="large"
            onClick={onOpen}
            sx={{
              ...(theme.palette.mode === "dark" && {
                bgcolor: theme.palette.primary.dark,
                "&:hover": {
                  bgcolor: theme.palette.primary.main,
                },
              }),
            }}
          >
            <AddIcon />
          </Fab>
        </Tooltip>
      </FloatingActionMenu>

      <Dialog
        open={isImportDialogOpen}
        onClose={() => setIsImportDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Import Debt CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {importMatches.length} matching accounts found,{" "}
            {importNewDebts.length} new accounts will be added.
          </Typography>
          {importMatches.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Choose which fields to override on matching debts:
              </Typography>
              <FormGroup>
                {debtOverrideFields.map((field) => (
                  <FormControlLabel
                    key={field.key}
                    control={
                      <Checkbox
                        checked={overrideSelections[field.key]}
                        onChange={() => handleToggleOverride(field.key)}
                      />
                    }
                    label={field.label}
                  />
                ))}
              </FormGroup>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={isImporting}
          >
            {isImporting ? "Importing..." : "Import"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isImportHistoryOpen}
        onClose={() => setIsImportHistoryOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Debt Import History</DialogTitle>
        <DialogContent>
          {importHistory.length === 0 ? (
            <Typography variant="body2" sx={{ py: 2 }}>
              No import history yet.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "12px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "8px" }}>
                      Date/Time
                    </th>
                    <th style={{ textAlign: "left", padding: "8px" }}>File</th>
                    <th style={{ textAlign: "right", padding: "8px" }}>
                      Added
                    </th>
                    <th style={{ textAlign: "right", padding: "8px" }}>
                      Updated
                    </th>
                    <th style={{ textAlign: "right", padding: "8px" }}>
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {importHistory.map((entry) => (
                    <tr
                      key={entry.id}
                      style={{ borderTop: "1px solid #e5e7eb" }}
                    >
                      <td style={{ padding: "8px" }}>
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: "8px" }}>
                        {entry.fileName || "—"}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {entry.addedCount}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {entry.updatedCount}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {entry.totalCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsImportHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LinkAccountDialog
        open={isLinkDialogOpen}
        debtToLink={debtToLink}
        accounts={accounts}
        onClose={() => {
          setIsLinkDialogOpen(false);
          setDebtToLink(null);
        }}
        onSelectAccount={handleSelectAccountToLink}
      />
      <ShareDebtModal
        open={shareOpen}
        debt={shareDebt}
        onClose={() => {
          setShareOpen(false);
          setShareDebt(null);
        }}
      />

      <PageShareModal
        open={pageShareOpen}
        onClose={() => {
          setPageShareOpen(false);
          const next = new URLSearchParams(searchParams);
          next.delete("share");
          setSearchParams(next, { replace: true });
        }}
        pageType="debts"
        debtItems={debts}
      />

      <LoadingTimerDebug
        loading={isLoading}
        label="Debts"
        details={{
          debts: debts.length,
          expenses: expenses.length,
          accounts: accounts.length,
          refreshing: isRefreshing,
          importing: isImporting,
          plaidLoading: isPlaidLoading,
          accountsLoading: isAccountsLoading,
          demoMode: demoService.isDemo(),
        }}
      />
    </div>
  );
};

export default DebtsPage;
